import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/base/EventCard";
import JsonLd from "@/components/base/JsonLd";
import usePageMeta from "@/hooks/usePageMeta";
import { events, categories, hotRanking } from "@/mocks/gossip";

export default function EventsPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const cat = params.get("cat") ?? "all";
  const [input, setInput] = useState(query);

  usePageMeta("复盘档案库 - 全部娱乐事件时间线复盘", {
    description:
      "91吃瓜网复盘档案库，收录明星恋情、影视剧组、综艺幕后、商务纠纷、塌房实锤与圈内恩怨的完整时间线梳理，支持关键词与主题筛选。",
    keywords: "91吃瓜网,复盘档案,事件时间线,娱乐八卦,实锤整理",
    canonicalPath: "/events",
    ogType: "website",
  });

  const list = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return events.filter((e) => {
      const matchCat = cat === "all" || e.categorySlug === cat;
      const matchKw =
        !kw ||
        e.title.toLowerCase().includes(kw) ||
        e.summary.toLowerCase().includes(kw) ||
        e.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [query, cat]);

  const applySearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setParams(next);
  };

  const setCat = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    setParams(next);
  };

  const listJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "91吃瓜网复盘档案库",
    description: "全部娱乐事件时间线复盘档案列表",
    hasPart: list.map((e) => ({
      "@type": "Article",
      headline: e.title,
      description: e.summary,
      datePublished: e.date,
    })),
  };

  return (
    <>
      <JsonLd
        data={[
          listJsonLd,
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://microtransport.cn/" },
              { "@type": "ListItem", position: 2, name: "复盘档案", item: "https://microtransport.cn/events" },
            ],
          },
        ]}
      />
      <SiteHeader />
      <main className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb items={[{ name: "首页", to: "/" }, { name: "复盘档案" }]} />
        </div>

        <header className="w-full px-4 md:px-8 pt-6 pb-8 border-b border-background-200">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.25em] text-primary-600 uppercase">
                <span className="w-6 h-px bg-primary-500"></span>
                复盘档案库
              </span>
              <h1 className="mt-3 font-heading font-black text-3xl md:text-[40px] leading-tight text-foreground-950">
                全部事件时间线复盘
              </h1>
              <p className="mt-3 font-label text-sm md:text-[15px] leading-relaxed text-foreground-600">
                共 {events.length} 篇深度复盘。每篇都从最早的可核实节点开始，梳理前因后果，并附上关系图与实锤清单。
              </p>
            </div>

            <form onSubmit={applySearch} className="w-full lg:w-auto">
              <div className="relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="search"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="搜索事件标题、关键词或标签"
                  aria-label="搜索复盘档案"
                  className="w-full lg:w-80 rounded-md border border-background-300 bg-background-100 py-3 pl-10 pr-24 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                <button
                  type="submit"
                  className="absolute right-1.5 top-1.5 rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCat("all")}
              className={`rounded-md px-4 py-2 font-label text-sm transition-colors cursor-pointer whitespace-nowrap ${
                cat === "all"
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setCat(c.slug)}
                className={`rounded-md px-4 py-2 font-label text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  cat === c.slug
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </header>

        <div className="w-full px-4 md:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <p className="font-label text-sm text-foreground-500">
                共找到 <span className="font-medium text-foreground-900">{list.length}</span> 篇复盘
                {query && <span>（关键词：{query}）</span>}
              </p>

              {list.length === 0 ? (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 p-12 text-center">
                  <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
                  <p className="mt-3 font-label text-sm text-foreground-600">
                    没有匹配的复盘档案，试试换个关键词或分类。
                  </p>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-5">
                  {list.map((e) => (
                    <EventCard key={e.id} event={e} variant="wide" />
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-600"></i>
                  热度榜
                </h2>
                <ol className="mt-4 space-y-3">
                  {hotRanking.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/event/${r.id}`}
                        className="group flex items-start gap-3 cursor-pointer"
                      >
                        <span
                          className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md font-heading font-bold text-xs ${
                            r.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-300 text-foreground-800"
                          }`}
                        >
                          {r.rank}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-label text-sm text-foreground-800 group-hover:text-primary-700 transition-colors line-clamp-2">
                            {r.title}
                          </span>
                          <span className="mt-0.5 flex items-center gap-1 font-label text-[11px] text-foreground-400">
                            <i className="ri-fire-line"></i>
                            {r.heat}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-accent-700"></i>
                  主题分类
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}